import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Eye, Trash2, Search } from "lucide-react";

const STATUSES = [
  { v: "new", label: "Nouveau", color: "bg-blue-100 text-blue-700" },
  { v: "quoted", label: "Devis envoyé", color: "bg-amber-100 text-amber-700" },
  { v: "confirmed", label: "Confirmé", color: "bg-emerald-100 text-emerald-700" },
  { v: "realized", label: "Réalisé", color: "bg-purple-100 text-purple-700" },
  { v: "cancelled", label: "Annulé", color: "bg-red-100 text-red-700" },
];

const statusBadge = (st) => STATUSES.find((s) => s.v === st) || { label: st, color: "bg-gray-100 text-gray-700" };

export default function AdminEventQuotes() {
  const [data, setData] = useState({ items: [], total: 0, status_counts: {} });
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const r = await api.get("/admin/event-quotes", { params });
      setData(r.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status]);

  const onOpen = async (q) => {
    const r = await api.get(`/admin/event-quotes/${q.id}`);
    setSelected({ ...r.data, _draft_status: r.data.status, _draft_response: r.data.admin_response || "", _draft_notes: r.data.admin_notes || "", _draft_amount: r.data.quoted_amount || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      await api.patch(`/admin/event-quotes/${selected.id}`, {
        status: selected._draft_status,
        admin_response: selected._draft_response,
        admin_notes: selected._draft_notes,
        quoted_amount: selected._draft_amount ? parseFloat(selected._draft_amount) : null,
      });
      toast.success("Demande mise à jour");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/admin/event-quotes/${id}`);
    toast.success("Supprimé");
    load();
  };

  const exportCSV = async () => {
    const r = await api.get("/admin/event-quotes/export", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "event-quotes.csv"; a.click();
  };

  return (
    <div className="space-y-6" data-testid="admin-event-quotes-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-bold text-2xl md:text-3xl text-[var(--dd-green)]">Dakar Events · Demandes</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">{data.total} demande(s) — statut & devis personnalisé</p>
        </div>
        <button onClick={exportCSV} className="btn-pill-outline" data-testid="export-quotes-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2" data-testid="quotes-status-filter">
        <button onClick={() => setFilter({ ...filter, status: "" })} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider ${filter.status === "" ? "bg-[var(--dd-green)] text-white" : "bg-white border border-[var(--dsf-border)] text-[var(--dd-green)]"}`}>Toutes ({data.status_counts.new + data.status_counts.quoted + data.status_counts.confirmed + data.status_counts.realized + data.status_counts.cancelled || 0})</button>
        {STATUSES.map((s) => (
          <button key={s.v} onClick={() => setFilter({ ...filter, status: s.v })} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider ${filter.status === s.v ? "bg-[var(--dd-green)] text-white" : "bg-white border border-[var(--dsf-border)] text-[var(--dd-green)]"}`} data-testid={`filter-status-${s.v}`}>
            {s.label} ({data.status_counts[s.v] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--dsf-text-muted)]" />
        <input
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="Recherche par nom, email, référence…"
          className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[var(--dsf-border)] text-sm focus:border-[var(--dd-green)]"
          data-testid="quotes-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-[var(--dsf-border)] rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 bg-[var(--dd-bone)] px-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[var(--dd-green)] font-bold">
          <div className="col-span-2">Référence</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Événement</div>
          <div className="col-span-2">Date & Invités</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {data.items.length === 0 && <div className="text-center py-16 text-[var(--dsf-text-muted)]">Aucune demande</div>}
        {data.items.map((q) => {
          const sb = statusBadge(q.status);
          return (
            <div key={q.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-4 border-t border-[var(--dsf-border)] items-center" data-testid={`quote-row-${q.id}`}>
              <div className="md:col-span-2 font-mono text-sm font-semibold text-[var(--dd-green)]">{q.quote_number}</div>
              <div className="md:col-span-3">
                <div className="font-semibold text-[var(--dd-green)]">{q.customer_name}</div>
                <div className="text-xs text-[var(--dsf-text-muted)] truncate">{q.customer_email}</div>
                <div className="text-xs text-[var(--dsf-text-muted)]">{q.customer_phone}</div>
              </div>
              <div className="md:col-span-2 text-sm capitalize">{q.event_type}<div className="text-xs text-[var(--dsf-text-muted)] truncate">{q.location}</div></div>
              <div className="md:col-span-2 text-sm"><div>{q.event_date}</div><div className="text-xs text-[var(--dsf-text-muted)]">{q.guest_count} invités</div></div>
              <div className="md:col-span-2">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${sb.color}`}>{sb.label}</span>
              </div>
              <div className="md:col-span-1 flex md:justify-end gap-2">
                <button onClick={() => onOpen(q)} className="w-8 h-8 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-green)] hover:bg-[var(--dd-bone)]" data-testid={`quote-view-${q.id}`}><Eye size={13} /></button>
                <button onClick={() => remove(q.id)} className="w-8 h-8 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-coral)] hover:bg-[var(--dd-bone)]" data-testid={`quote-delete-${q.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {selected && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-[var(--dd-green)]">{selected.quote_number} — {selected.customer_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Info label="Email" value={selected.customer_email} />
                <Info label="Téléphone" value={selected.customer_phone} />
                <Info label="Société" value={selected.company || "—"} />
                <Info label="Date" value={selected.event_date} />
                <Info label="Type" value={selected.event_type} />
                <Info label="Invités" value={selected.guest_count} />
                <Info label="Budget" value={selected.budget || "—"} />
                <Info label="Lieu" value={selected.location} />
              </div>

              <div>
                <Lbl>Services demandés</Lbl>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(selected.services || []).length === 0 && <span className="text-xs text-[var(--dsf-text-muted)]">Aucun service spécifié</span>}
                  {(selected.services || []).map((s) => (
                    <span key={s} className="text-[11px] bg-[var(--dd-green-soft)] text-[var(--dd-green)] px-3 py-1 rounded-full font-mono">{s}</span>
                  ))}
                </div>
              </div>

              <div>
                <Lbl>Message du client</Lbl>
                <div className="mt-2 p-4 bg-[var(--dd-bone)] rounded-xl text-sm whitespace-pre-wrap">{selected.message}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Lbl>Statut</Lbl>
                  <select value={selected._draft_status} onChange={(e) => setSelected({ ...selected, _draft_status: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-2 text-sm" data-testid="quote-modal-status">
                    {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <Lbl>Montant du devis (USD)</Lbl>
                  <input type="number" step="0.01" value={selected._draft_amount} onChange={(e) => setSelected({ ...selected, _draft_amount: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-2 text-sm" data-testid="quote-modal-amount" placeholder="ex: 12500" />
                </div>
              </div>

              <div>
                <Lbl>Réponse au client</Lbl>
                <textarea rows={4} value={selected._draft_response} onChange={(e) => setSelected({ ...selected, _draft_response: e.target.value })} placeholder="Message qui sera envoyé/communiqué au client…" className="w-full border border-black/10 rounded-xl px-3 py-2 mt-2 text-sm" data-testid="quote-modal-response" />
              </div>

              <div>
                <Lbl>Notes internes</Lbl>
                <textarea rows={3} value={selected._draft_notes} onChange={(e) => setSelected({ ...selected, _draft_notes: e.target.value })} placeholder="Notes visibles uniquement par l'équipe interne…" className="w-full border border-black/10 rounded-xl px-3 py-2 mt-2 text-sm" data-testid="quote-modal-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-[var(--dsf-border)]">
              <button onClick={() => setOpen(false)} className="btn-pill-outline">Annuler</button>
              <button onClick={save} className="btn-mustard" data-testid="quote-modal-save">Enregistrer</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Lbl = ({ children }) => (<label className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] font-bold">{children}</label>);
const Info = ({ label, value }) => (
  <div>
    <Lbl>{label}</Lbl>
    <div className="font-medium text-[var(--dd-green)] text-sm mt-0.5 break-words">{value}</div>
  </div>
);
